import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Trophy, Users, Award, TrendingUp, AlertCircle, Loader2, Copy,
  CheckCircle2, ArrowLeft, BarChart3, Flame, Calendar,
} from "lucide-react";
import { apiGet } from "../lib/api";

// Local lavender palette to stay consistent with the rest of the admin area.
const ADMIN = {
  bg: "#FBF8F2",
  card: "#FFFFFF",
  border: "#E5DCEF",
  text: "#1A1432",
  muted: "#6B5F8A",
  primary: "#5A3FC0",
  primary_dark: "#2C1862",
  gold: "#C8A04A",
  rose: "#9E2A48",
  green: "#1A6B3A",
};

function StatCard({ label, value, suffix, icon: Icon, tone = "neutral", testId }) {
  const palette = {
    neutral: { bg: "#F4F0FA", fg: ADMIN.primary_dark },
    success: { bg: "#E8F5EE", fg: ADMIN.green },
    danger:  { bg: "#FCE9EE", fg: ADMIN.rose },
    gold:    { bg: "#FFF7E5", fg: "#7A4F00" },
  }[tone];
  return (
    <div data-testid={testId}
         className="rounded-2xl p-5 border"
         style={{ background: ADMIN.card, borderColor: ADMIN.border }}>
      <div className="flex items-center gap-2 mb-3">
        <span className="h-9 w-9 rounded-xl grid place-items-center"
              style={{ background: palette.bg, color: palette.fg }}>
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-[11px] uppercase tracking-wider"
              style={{ color: ADMIN.muted }}>{label}</span>
      </div>
      <p className="font-serif text-3xl" style={{ color: ADMIN.text }}>
        {value}
        {suffix ? <span className="text-base ml-1" style={{ color: ADMIN.muted }}>{suffix}</span> : null}
      </p>
    </div>
  );
}

function VolumeChart({ data }) {
  const max = useMemo(() => Math.max(1, ...data.map((d) => d.count)), [data]);
  return (
    <div data-testid="vip-simulator-volume-chart"
         className="rounded-2xl p-5 border"
         style={{ background: ADMIN.card, borderColor: ADMIN.border }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-xl" style={{ color: ADMIN.text }}>
          Volume giornaliero
        </h3>
        <span className="text-xs inline-flex items-center gap-1"
              style={{ color: ADMIN.muted }}>
          <Calendar className="h-3 w-3" /> ultimi {data.length} giorni
        </span>
      </div>
      <div className="flex items-end gap-1 h-32">
        {data.map((d) => {
          const h = max ? (d.count / max) * 100 : 0;
          return (
            <div key={d.date} className="flex-1 flex flex-col items-stretch group"
                 title={`${d.date}: ${d.count}`}>
              <div className="flex-1 flex items-end">
                <div className="w-full rounded-t-md transition-all"
                     style={{
                       height: `${h}%`,
                       minHeight: d.count ? "4px" : "0",
                       background: d.count
                         ? `linear-gradient(180deg, ${ADMIN.primary} 0%, ${ADMIN.primary_dark} 100%)`
                         : "transparent",
                     }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] mt-2"
           style={{ color: ADMIN.muted }}>
        <span>{data[0]?.date?.slice(5) || ""}</span>
        <span>{data[data.length - 1]?.date?.slice(5) || ""}</span>
      </div>
    </div>
  );
}

function PerSlotTable({ slots }) {
  if (!slots?.length) {
    return (
      <p className="text-sm" style={{ color: ADMIN.muted }}>
        Nessun dato disponibile.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="vip-simulator-per-slot-table">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ color: ADMIN.muted }}>
            <th className="text-left py-2 font-medium">Modulo</th>
            <th className="text-right py-2 font-medium">Tentativi</th>
            <th className="text-right py-2 font-medium">Media corretta</th>
            <th className="text-right py-2 font-medium">Pass rate</th>
          </tr>
        </thead>
        <tbody>
          {slots.map((s) => {
            const tone = s.pass_rate >= 60 ? ADMIN.green
                       : s.pass_rate >= 40 ? "#7A4F00"
                       : ADMIN.rose;
            return (
              <tr key={s.slot_id} style={{ borderTop: `1px solid ${ADMIN.border}` }}>
                <td className="py-3" style={{ color: ADMIN.text }}>
                  <strong>{s.label_it}</strong>
                </td>
                <td className="py-3 text-right" style={{ color: ADMIN.muted }}>{s.attempts}</td>
                <td className="py-3 text-right" style={{ color: ADMIN.text }}>{s.avg_correct} / {s.max_score}</td>
                <td className="py-3 text-right font-semibold" style={{ color: tone }}>
                  {s.pass_rate}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function HardestList({ questions }) {
  if (!questions?.length) {
    return (
      <p className="text-sm" style={{ color: ADMIN.muted }}>
        Servono almeno 3 tentativi per la stessa domanda per generare statistiche utili. Riprova quando ci saranno più dati.
      </p>
    );
  }
  return (
    <ol className="space-y-2" data-testid="vip-simulator-hardest-list">
      {questions.map((q, i) => (
        <li key={q.uid}
            data-testid={`vip-simulator-hardest-${i}`}
            className="rounded-xl border p-3 flex items-start gap-3"
            style={{ borderColor: ADMIN.border, background: "#FAF7FE" }}>
          <span className="h-7 w-7 rounded-full grid place-items-center text-xs font-bold shrink-0"
                style={{ background: ADMIN.primary, color: "white" }}>
            {i + 1}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm leading-snug" style={{ color: ADMIN.text }}>
              {q.stem || <span className="italic" style={{ color: ADMIN.muted }}>(senza testo)</span>}
            </p>
            <div className="flex items-center gap-2 mt-1 flex-wrap text-[11px]">
              <span className="px-2 py-0.5 rounded-full font-semibold"
                    style={{ background: "#FCE9EE", color: ADMIN.rose }}>
                {q.wrong_rate}% errate
              </span>
              <span style={{ color: ADMIN.muted }}>
                ({q.wrong}/{q.seen})
              </span>
              {q.slot_label_it ? (
                <span style={{ color: ADMIN.muted }}>· {q.slot_label_it}</span>
              ) : null}
              {q.lesson_id ? (
                <Link to={`/admin/vip/lessons/${q.lesson_id}`}
                      className="ml-auto underline"
                      style={{ color: ADMIN.primary }}>
                  Apri lezione
                </Link>
              ) : null}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function SocialProofCard({ socialProof, totals, passRate }) {
  const [copiedLang, setCopiedLang] = useState(null);
  const copy = async (lang) => {
    try {
      await navigator.clipboard.writeText(socialProof[lang] || "");
      setCopiedLang(lang);
      setTimeout(() => setCopiedLang(null), 2000);
    } catch { /* clipboard denied — ignore */ }
  };
  return (
    <div data-testid="vip-simulator-social-proof"
         className="rounded-2xl shadow-xl p-6 text-white"
         style={{ background: `linear-gradient(135deg, ${ADMIN.primary_dark} 0%, ${ADMIN.primary} 60%, #B45309 100%)` }}>
      <p className="text-[10px] uppercase tracking-[0.22em] opacity-90 mb-2 inline-flex items-center gap-2">
        <Trophy className="h-3 w-3" /> Social proof publicabile
      </p>
      <h3 className="font-serif text-2xl mb-3 leading-snug">
        {passRate}% pass rate · {totals.passed} medici certificati
      </h3>
      <p className="opacity-90 text-sm mb-4 leading-relaxed">
        Copia queste frasi per landing, social media o email marketing.
      </p>
      <div className="space-y-2">
        {["it", "es"].map((lang) => (
          <div key={lang}
               className="rounded-xl px-3 py-2.5 flex items-start gap-2"
               style={{ background: "rgba(255,255,255,0.14)", backdropFilter: "blur(8px)" }}>
            <span className="text-[10px] uppercase tracking-wider font-semibold mt-0.5"
                  style={{ color: "#FFE08A" }}>{lang}</span>
            <p className="flex-1 text-sm leading-snug">{socialProof[lang]}</p>
            <button onClick={() => copy(lang)}
                    data-testid={`vip-simulator-social-copy-${lang}`}
                    className="ml-2 shrink-0 px-2 py-1 rounded-md text-xs font-medium inline-flex items-center gap-1 hover:bg-white/10 transition-colors">
              {copiedLang === lang
                ? <><CheckCircle2 className="h-3 w-3" /> Copiato</>
                : <><Copy className="h-3 w-3" /> Copia</>}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function VIPAdminSimulator() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiGet(`/admin/vip/simulator/dashboard?days=${days}`)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.detail || "Errore caricamento");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center"
           style={{ background: ADMIN.bg }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: ADMIN.primary }} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center"
           style={{ background: ADMIN.bg }}>
        <div className="rounded-2xl bg-white p-6 border max-w-md w-full"
             style={{ borderColor: ADMIN.border }}>
          <div className="flex items-center gap-2 mb-2" style={{ color: ADMIN.rose }}>
            <AlertCircle className="h-5 w-5" />
            <strong>{error}</strong>
          </div>
          <Link to="/admin/vip" className="text-sm underline"
                style={{ color: ADMIN.primary }}>← Torna al pannello</Link>
        </div>
      </div>
    );
  }

  const totals = data?.totals || {};
  return (
    <div className="min-h-screen px-4 sm:px-8 py-8"
         style={{ background: ADMIN.bg }}
         data-testid="vip-admin-simulator-page">
      <div className="max-w-7xl mx-auto">
        <Link to="/admin/vip" data-testid="vip-admin-simulator-back"
              className="inline-flex items-center gap-1.5 text-sm mb-4 hover:underline"
              style={{ color: ADMIN.muted }}>
          <ArrowLeft className="h-4 w-4" /> Pannello Academia
        </Link>
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] mb-1 inline-flex items-center gap-2"
               style={{ color: ADMIN.muted }}>
              <BarChart3 className="h-3 w-3" /> Dashboard simulatore
            </p>
            <h1 className="font-serif text-4xl" style={{ color: ADMIN.text }}>
              Simulatore Reale · Statistiche
            </h1>
          </div>
          <div className="inline-flex rounded-xl border overflow-hidden"
               style={{ borderColor: ADMIN.border, background: ADMIN.card }}>
            {[7, 30, 90].map((d) => (
              <button key={d}
                      onClick={() => setDays(d)}
                      data-testid={`vip-simulator-range-${d}`}
                      className="px-3 py-2 text-sm transition-colors"
                      style={{
                        background: days === d ? ADMIN.primary : "transparent",
                        color: days === d ? "white" : ADMIN.text,
                      }}>
                {d}gg
              </button>
            ))}
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard testId="vip-simulator-kpi-passrate"
                    label="Pass rate globale"
                    value={data.pass_rate} suffix="%"
                    icon={TrendingUp}
                    tone={data.pass_rate >= 60 ? "success"
                          : data.pass_rate >= 30 ? "gold" : "danger"} />
          <StatCard testId="vip-simulator-kpi-submitted"
                    label="Tentativi consegnati"
                    value={totals.submitted || 0} icon={CheckCircle2} />
          <StatCard testId="vip-simulator-kpi-users"
                    label="Utenti attivi"
                    value={totals.users_attempted || 0} icon={Users} />
          <StatCard testId="vip-simulator-kpi-certs"
                    label="Certificati emessi"
                    value={totals.certificates_issued || 0} icon={Award}
                    tone="gold" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                      className="lg:col-span-2">
            <VolumeChart data={data.volume_by_day || []} />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 }}>
            <SocialProofCard socialProof={data.social_proof || {}}
                             totals={totals} passRate={data.pass_rate} />
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                      className="rounded-2xl p-5 border"
                      style={{ background: ADMIN.card, borderColor: ADMIN.border }}>
            <h3 className="font-serif text-xl mb-4 inline-flex items-center gap-2"
                style={{ color: ADMIN.text }}>
              <BarChart3 className="h-4 w-4" style={{ color: ADMIN.primary }} />
              Performance per modulo
            </h3>
            <PerSlotTable slots={data.per_slot} />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 }}
                      className="rounded-2xl p-5 border"
                      style={{ background: ADMIN.card, borderColor: ADMIN.border }}>
            <h3 className="font-serif text-xl mb-4 inline-flex items-center gap-2"
                style={{ color: ADMIN.text }}>
              <Flame className="h-4 w-4" style={{ color: ADMIN.rose }} />
              Top 10 domande più sbagliate
            </h3>
            <HardestList questions={data.hardest_questions} />
          </motion.div>
        </div>
      </div>
    </div>
  );
}
